import React, { useMemo } from 'react';
import { ZOOM, isWeekend, dayOfWeekShort, diffDays, startOfDay, xDias, anchoDias } from './constants';
import { escalaGantt } from './escala';
import { dateKey } from '../utils/helpers';

// Que unidades pinta cada fila segun la escala. Dos escalas que comparten tipo
// en una fila (Dia y Semana tienen los mismos meses arriba) la pintan una sola
// vez durante el viaje de la camara, sin doble texto.
const tipoArriba = (zoom) => (zoom === 'dia' || zoom === 'semana' ? 'meses' : 'anios');

// Escalas de cada capa, calculadas una vez por ventana (no en cada cuadro).
export const useEscalasPorCapa = (capas, firstDay, totalDays) => {
  const clave = capas.map((c) => c.zoom).join('|');
  const firstMs = firstDay.getTime();
  return useMemo(() => {
    const mapa = {};
    clave.split('|').forEach((z) => { mapa[z] = escalaGantt(z, new Date(firstMs), totalDays); });
    return mapa;
  }, [clave, firstMs, totalDays]);
};

// Unidades a pintar: durante el viaje, solo las que la camara llega a mostrar.
export const enRango = (rango) => (u) => !rango || (u.offset + u.width >= rango.desde && u.offset <= rango.hasta);

// Opacidad de una capa durante el viaje, con el avance --p que escribe la
// camara: la escala que sale se apaga en la primera mitad y la que entra
// aparece en la segunda. Una capa fija no se toca.
export const OPACIDAD_CAPA = {
  fija: undefined,
  sale: 'max(0, calc(1 - var(--p, 1) * 2))',
  entra: 'clamp(0, calc((var(--p, 1) - 0.35) * 2), 1)',
};

// Agrupa las capas por el tipo de unidad que pintan. Un tipo que esta en todas
// las capas (las dos escalas lo comparten) queda fijo, con los textos de la
// escala de llegada; uno que esta en una sola sigue el rol de esa capa (sale o
// entra).
export const agruparCapas = (lista, tipoYUnidades) => {
  const porTipo = new Map();
  lista.forEach((c) => {
    const r = tipoYUnidades(c);
    if (!r) return;
    const g = porTipo.get(r.tipo) || { ...r, rol: c.rol, capas: 0 };
    g.zoom = c.zoom;
    g.capas += 1;
    porTipo.set(r.tipo, g);
  });
  return [...porTipo.values()].map((g) => ({ ...g, rol: g.capas === lista.length ? 'fija' : g.rol }));
};

/**
 * Encabezado temporal del Gantt: 2 filas sticky que cambian con el zoom
 * (ver escala.js). Día: meses + días. Semana: meses + semanas. Mes: años +
 * meses. Trimestre: años + trimestres. Hoy se marca con un círculo rose en
 * la vista Día y con una marca en la fila de abajo en las demás.
 *
 * Se ubica con --dw (ver xDias). `capas` trae las dos escalas mientras la
 * camara viaja; cada capa decide sus textos con el ancho de dia de su escala.
 */
const GanttHeader = ({ firstDay, totalDays, zoom = 'semana', capas, rango }) => {
  const lista = capas || [{ zoom, rol: 'fija' }];
  const escalas = useEscalasPorCapa(lista, firstDay, totalDays);
  const today = startOfDay(new Date());
  const todayKey = dateKey(today);
  const todayOffset = diffDays(today, firstDay);
  const hoyVisible = todayOffset >= 0 && todayOffset < totalDays;
  const visible = enRango(rango);

  const filas = (fila) => agruparCapas(lista, (c) => {
    const escala = escalas[c.zoom];
    if (!escala) return null;
    return { tipo: fila === 'arriba' ? tipoArriba(c.zoom) : escala.tipoAbajo, unidades: escala[fila] };
  });

  return (
    <div className="sticky top-0 z-20 bg-white border-b border-slate-200/60">
      {/* Fila de arriba: meses o años */}
      <div className="relative h-8 border-b border-slate-200/40 bg-slate-50/40">
        {filas('arriba').map((f) => {
          const dw = ZOOM[f.zoom].px;
          return (
            <div key={f.tipo} className="absolute inset-0" style={{ opacity: OPACIDAD_CAPA[f.rol] }}>
              {f.unidades.filter(visible).map((u) => (
                <div
                  key={u.key}
                  className="absolute top-0 bottom-0 flex items-center border-r border-slate-200/60 overflow-hidden"
                  style={{ left: xDias(u.offset), width: anchoDias(u.width) }}
                >
                  <span className="px-2 text-[11px] font-semibold text-slate-600 whitespace-nowrap">
                    {u.width * dw >= 110 ? u.label : (u.labelMedio || u.labelCorto)}
                  </span>
                </div>
              ))}
            </div>
          );
        })}
      </div>

      {/* Fila de abajo */}
      <div className="relative h-8">
        {filas('abajo').map((f) => {
          const dw = ZOOM[f.zoom].px;
          return (
            <div key={f.tipo} className="absolute inset-0" style={{ opacity: OPACIDAD_CAPA[f.rol] }}>
              {f.tipo === 'dia' && f.unidades.filter(visible).map((u) => {
                const d = u.fecha;
                const isToday = dateKey(d) === todayKey;
                const we = isWeekend(d);
                return (
                  <div
                    key={u.key}
                    className={`absolute top-0 bottom-0 flex items-center justify-center text-[10px] gap-1 border-r border-slate-200/40 ${
                      we ? 'bg-slate-50/60 text-slate-400' : 'text-slate-500'
                    } ${d.getDate() === 1 ? 'border-l border-l-slate-300' : ''}`}
                    style={{ left: xDias(u.offset), width: anchoDias(1) }}
                  >
                    {dw >= 30 && <span>{dayOfWeekShort(d)}</span>}
                    {isToday ? (
                      <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-rose-500 text-white font-semibold text-[10px]">
                        {d.getDate()}
                      </span>
                    ) : (
                      <span className="tabular-nums">{d.getDate()}</span>
                    )}
                  </div>
                );
              })}

              {f.tipo !== 'dia' && f.unidades.filter(visible).map((u) => {
                const w = u.width * dw;
                const principal = f.tipo === 'mes'
                  ? (w >= 70 ? u.labelMedio : u.labelCorto)
                  : u.label;
                // La unidad que contiene hoy se marca en rose (en vez de una
                // etiqueta "Hoy" encima, que tapaba el texto de la unidad).
                const esHoy = hoyVisible && todayOffset >= u.offset && todayOffset < u.offset + u.width;
                return (
                  <div
                    key={u.key}
                    className="absolute top-0 bottom-0 flex items-center gap-1.5 px-2 text-[10px] border-r border-slate-200/40 whitespace-nowrap overflow-hidden"
                    style={{ left: xDias(u.offset), width: anchoDias(u.width) }}
                    title={u.detalle ? `${u.label} · ${u.detalle}` : undefined}
                  >
                    <span className={`font-semibold ${esHoy ? 'text-rose-600' : 'text-slate-600'}`}>{principal}</span>
                    {u.detalle && w >= 90 && <span className="text-slate-400 tabular-nums">{u.detalle}</span>}
                  </div>
                );
              })}

              {f.tipo !== 'dia' && hoyVisible && (
                <span
                  className="absolute -bottom-[3px] w-1.5 h-1.5 -translate-x-1/2 rounded-full bg-rose-500 ring-2 ring-white pointer-events-none"
                  style={{ left: xDias(todayOffset + 0.5) }}
                  title="Hoy"
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default GanttHeader;
